import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  Change,
  ChangeType,
  ChangeRequestType,
  ChangePriority,
  CreateChangeRequest,
  UpdateChangeRequest,
  AddChangeItemRequest
} from '../../models/file-vault.models';

export interface ChangeFormData {
  changeType: ChangeType;
  requestType: ChangeRequestType;
  title: string;
  description: string;
  justification: string;
  priority: ChangePriority;
  dueDate: Date | null;
  effectiveDate: Date | null;
  assigneeId: number | null;
}

@Component({
  selector: 'fv-change-create',
  standalone: true,
  template: `
    <div class="change-form-container">
      <div class="form-header">
        <h2>{{ isEditMode ? 'Edit Change' : 'Create New Change' }}</h2>
        <p class="subtitle" *ngIf="!isEditMode">
          Create an engineering change request or order to track modifications to parts and documents.
        </p>
      </div>

      <form (ngSubmit)="onSubmit()" #changeForm="ngForm">
        <!-- Change Type -->
        <div class="form-row">
          <div class="form-group half">
            <label for="changeType">Change Type *</label>
            <kendo-dropdownlist
              id="changeType"
              [(ngModel)]="formData.changeType"
              name="changeType"
              [data]="changeTypes"
              textField="text"
              valueField="value"
              [valuePrimitive]="true"
              [disabled]="isEditMode"
              required>
            </kendo-dropdownlist>
            <span class="help-text">{{ getTypeDescription(formData.changeType) }}</span>
          </div>

          <div class="form-group half">
            <label for="priority">Priority *</label>
            <kendo-dropdownlist
              id="priority"
              [(ngModel)]="formData.priority"
              name="priority"
              [data]="priorities"
              textField="text"
              valueField="value"
              [valuePrimitive]="true"
              required>
            </kendo-dropdownlist>
          </div>
        </div>

        <!-- Request Type (ECR only) -->
        <div class="form-group" *ngIf="formData.changeType === ChangeType.ECR">
          <label for="requestType">Request Type *</label>
          <kendo-dropdownlist
            id="requestType"
            [(ngModel)]="formData.requestType"
            name="requestType"
            [data]="requestTypes"
            textField="text"
            valueField="value"
            [valuePrimitive]="true"
            required>
          </kendo-dropdownlist>
          <span class="help-text" *ngIf="formData.requestType === ChangeRequestType.NewCreation">
            No affected items required — engineering will add them when the ECO is created.
          </span>
          <span class="help-text" *ngIf="formData.requestType === ChangeRequestType.Modification">
            Add the parts or documents being changed as affected items before submitting.
          </span>
          <span class="help-text" *ngIf="formData.requestType === ChangeRequestType.Obsolescence">
            Add the parts or documents being retired as affected items before submitting.
          </span>
        </div>

        <!-- Title -->
        <div class="form-group">
          <label for="title">Title *</label>
          <input kendoTextBox
                 id="title"
                 [(ngModel)]="formData.title"
                 name="title"
                 placeholder="Brief description of the change..."
                 required
                 maxlength="200" />
          <span class="char-count">{{ formData.title.length }}/200</span>
        </div>

        <!-- Description -->
        <div class="form-group">
          <label for="description">Description</label>
          <textarea kendoTextArea
                    id="description"
                    [(ngModel)]="formData.description"
                    name="description"
                    [rows]="4"
                    placeholder="Detailed description of what is being changed and why...">
          </textarea>
        </div>

        <!-- Justification -->
        <div class="form-group">
          <label for="justification">Justification</label>
          <textarea kendoTextArea
                    id="justification"
                    [(ngModel)]="formData.justification"
                    name="justification"
                    [rows]="3"
                    placeholder="Business or technical justification for this change...">
          </textarea>
        </div>

        <!-- Dates -->
        <div class="form-row">
          <div class="form-group half">
            <label for="dueDate">Due Date</label>
            <kendo-datepicker
              id="dueDate"
              [(ngModel)]="formData.dueDate"
              name="dueDate"
              [min]="minDate"
              format="MM/dd/yyyy">
            </kendo-datepicker>
          </div>

          <div class="form-group half">
            <label for="effectiveDate">Effective Date</label>
            <kendo-datepicker
              id="effectiveDate"
              [(ngModel)]="formData.effectiveDate"
              name="effectiveDate"
              format="MM/dd/yyyy">
            </kendo-datepicker>
            <span class="help-text">When the change should take effect</span>
          </div>
        </div>

        <!-- Type-specific info -->
        <div class="info-box" *ngIf="formData.changeType === ChangeType.ECR">
          <span class="info-icon">ℹ️</span>
          <div>
            <strong>Engineering Change Request (ECR)</strong>
            <p>An ECR is used to propose a change. Once approved, it can be converted to an ECO for formal implementation.</p>
          </div>
        </div>

        <div class="info-box eco" *ngIf="formData.changeType === ChangeType.ECO">
          <span class="info-icon">📋</span>
          <div>
            <strong>Engineering Change Order (ECO)</strong>
            <p>An ECO is a formal change order that tracks implementation of approved changes to parts and documents.</p>
          </div>
        </div>

        <div class="info-box deviation" *ngIf="formData.changeType === ChangeType.Deviation">
          <span class="info-icon">⚡</span>
          <div>
            <strong>Deviation</strong>
            <p>A temporary departure from approved specifications. Use for limited quantities or time periods.</p>
          </div>
        </div>

        <!-- Actions -->
        <div class="form-actions">
          <button kendoButton type="button" [look]="'flat'" (click)="onCancel()">
            Cancel
          </button>
          <div class="right-actions">
            <button kendoButton type="button" [look]="'outline'"
                    (click)="onSaveDraft()"
                    [disabled]="!canSave || isSaving">
              {{ isSaving && savingMode === 'draft' ? 'Saving...' : 'Save as Draft' }}
            </button>
            <button kendoButton type="submit" [themeColor]="'primary'"
                    [disabled]="!canSave || isSaving"
                    [title]="!canSubmit && !isEditMode ? 'Add affected items to submit, or save as draft first' : ''">
              {{ submitButtonLabel }}
            </button>
          </div>
        </div>

        <!-- Validation hint when no affected items (not shown for NewCreation ECRs) -->
        <div class="info-hint" *ngIf="!canSubmit && !isEditMode && formData.requestType !== ChangeRequestType.NewCreation">
          <span class="info-icon">💡</span>
          No affected items provided — click <strong>Create &amp; Submit</strong> to save as a draft, then add affected items before submitting.
        </div>
      </form>

      <!-- Error message -->
      <div class="error-message" *ngIf="errorMessage">
        <span class="error-icon">⚠️</span>
        {{ errorMessage }}
      </div>
    </div>
  `,
  styles: [`
    .change-form-container {
      max-width: 700px;
      margin: 0 auto;
      padding: 24px;
    }

    .form-header {
      margin-bottom: 24px;
    }

    .form-header h2 {
      margin: 0 0 8px 0;
      font-size: 24px;
      font-weight: 500;
    }

    .subtitle {
      margin: 0;
      color: var(--fv-text-secondary);
    }

    .form-group {
      margin-bottom: 20px;
    }

    .form-row {
      display: flex;
      gap: 16px;
    }

    .form-group.half {
      flex: 1;
    }

    .form-group label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      font-size: 14px;
    }

    .form-group input,
    .form-group textarea,
    .form-group kendo-dropdownlist,
    .form-group kendo-datepicker {
      width: 100%;
    }

    .char-count {
      display: block;
      text-align: right;
      font-size: 11px;
      color: var(--fv-text-muted);
      margin-top: 4px;
    }

    .help-text {
      display: block;
      font-size: 12px;
      color: var(--fv-text-muted);
      margin-top: 4px;
    }

    .info-box {
      display: flex;
      gap: 12px;
      padding: 16px;
      background: var(--fv-status-info-bg);
      border-radius: 8px;
      margin-bottom: 24px;
    }

    .info-box.eco {
      background: var(--fv-status-warning-bg);
    }

    .info-box.deviation {
      background: var(--fv-status-error-bg);
    }

    .info-icon {
      font-size: 20px;
      flex-shrink: 0;
    }

    .info-box strong {
      display: block;
      margin-bottom: 4px;
    }

    .info-box p {
      margin: 0;
      font-size: 13px;
      color: var(--fv-text-secondary);
    }

    .form-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 16px;
      border-top: 1px solid var(--fv-border);
      margin-top: 24px;
    }

    .right-actions {
      display: flex;
      gap: 8px;
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      background: var(--fv-status-error-bg);
      color: var(--fv-status-error-fg);
      border-radius: 4px;
      margin-top: 16px;
    }

    .error-icon {
      flex-shrink: 0;
    }

    .info-hint {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px;
      background: var(--fv-status-warning-bg);
      color: var(--fv-status-warning-fg);
      border-radius: 4px;
      margin-top: 12px;
      font-size: 13px;
    }

    @media (max-width: 600px) {
      .form-row {
        flex-direction: column;
        gap: 0;
      }

      .form-group.half {
        flex: none;
      }

      .form-actions {
        flex-direction: column;
        gap: 12px;
      }

      .right-actions {
        width: 100%;
        flex-direction: column;
      }

      .right-actions button {
        width: 100%;
      }
    }
  `],
  imports: [CommonModule, FormsModule, ButtonsModule, InputsModule, DropDownsModule, DateInputsModule]
})
export class ChangeCreateComponent implements OnInit {
  private api = inject(FileVaultApiService);

  /** Existing change to edit (if editing) */
  @Input() existingChange?: Change;

  /** Initial affected items to add */
  @Input() initialAffectedItems?: AddChangeItemRequest[];

  /** Emitted when change is created/saved */
  @Output() saved = new EventEmitter<Change>();

  /** Emitted when cancel is clicked */
  @Output() cancelled = new EventEmitter<void>();

  ChangeType = ChangeType;
  ChangeRequestType = ChangeRequestType;

  formData: ChangeFormData = {
    changeType: ChangeType.ECR,
    requestType: ChangeRequestType.Modification,
    title: '',
    description: '',
    justification: '',
    priority: ChangePriority.Medium,
    dueDate: null,
    effectiveDate: null,
    assigneeId: null
  };

  isSaving = false;
  savingMode: 'draft' | 'submit' = 'draft';
  errorMessage = '';
  minDate = new Date();

  changeTypes = [
    { text: 'Engineering Change Request (ECR)', value: ChangeType.ECR },
    { text: 'Engineering Change Order (ECO)', value: ChangeType.ECO },
    { text: 'Deviation', value: ChangeType.Deviation },
    { text: 'Engineering Change Notice (ECN)', value: ChangeType.ECN }
  ];

  priorities = [
    { text: 'Low', value: ChangePriority.Low },
    { text: 'Medium', value: ChangePriority.Medium },
    { text: 'High', value: ChangePriority.High },
    { text: 'Critical', value: ChangePriority.Critical }
  ];

  requestTypes = [
    { text: 'Modification / Improvement', value: ChangeRequestType.Modification },
    { text: 'New Part / Document Creation', value: ChangeRequestType.NewCreation },
    { text: 'Obsolescence / Retirement', value: ChangeRequestType.Obsolescence }
  ];

  get isEditMode(): boolean {
    return !!this.existingChange;
  }

  get canSave(): boolean {
    return this.formData.title.trim().length > 0;
  }

  /** Whether the change can be submitted (has affected items, or is a NewCreation ECR) */
  get canSubmit(): boolean {
    // NewCreation ECRs don't need affected items — just title + description
    if (this.formData.changeType === ChangeType.ECR &&
        this.formData.requestType === ChangeRequestType.NewCreation) {
      return this.formData.description.trim().length > 0;
    }
    return !!this.initialAffectedItems && this.initialAffectedItems.length > 0;
  }

  get submitButtonLabel(): string {
    if (this.isEditMode) {
      return this.isSaving && this.savingMode === 'submit' ? 'Saving...' : 'Save Changes';
    }
    if (this.isSaving && this.savingMode === 'submit') {
      return this.canSubmit ? 'Creating & Submitting...' : 'Creating...';
    }
    return this.canSubmit ? 'Create & Submit' : 'Create as Draft';
  }

  ngOnInit(): void {
    if (this.existingChange) {
      this.formData = {
        changeType: this.existingChange.changeType,
        requestType: this.existingChange.requestType ?? ChangeRequestType.Modification,
        title: this.existingChange.title,
        description: this.existingChange.description || '',
        justification: this.existingChange.justification || '',
        priority: this.existingChange.priority,
        dueDate: this.existingChange.dueDate ? new Date(this.existingChange.dueDate) : null,
        effectiveDate: this.existingChange.effectiveDate ? new Date(this.existingChange.effectiveDate) : null,
        assigneeId: this.existingChange.assigneeId || null
      };
    }
  }

  async onSaveDraft(): Promise<void> {
    this.savingMode = 'draft';
    await this.save(false);
  }

  async onSubmit(): Promise<void> {
    this.savingMode = 'submit';
    await this.save(!this.isEditMode);
  }

  private async save(submitAfterCreate: boolean): Promise<void> {
    if (!this.canSave) return;

    this.isSaving = true;
    this.errorMessage = '';

    try {
      let change: Change | undefined;

      if (this.isEditMode && this.existingChange) {
        // Update existing change
        const request: UpdateChangeRequest = {
          title: this.formData.title,
          description: this.formData.description || undefined,
          justification: this.formData.justification || undefined,
          priority: this.formData.priority,
          requestType: this.formData.requestType,
          dueDate: this.formData.dueDate || undefined,
          effectiveDate: this.formData.effectiveDate || undefined,
          assigneeId: this.formData.assigneeId || undefined
        };

        change = await this.api.updateChange(this.existingChange.id, request).toPromise();
      } else {
        // Create new change
        const request: CreateChangeRequest = {
          changeType: this.formData.changeType,
          requestType: this.formData.requestType,
          title: this.formData.title,
          description: this.formData.description || undefined,
          justification: this.formData.justification || undefined,
          priority: this.formData.priority,
          dueDate: this.formData.dueDate || undefined,
          effectiveDate: this.formData.effectiveDate || undefined,
          assigneeId: this.formData.assigneeId || undefined,
          affectedItems: this.initialAffectedItems
        };

        change = await this.api.createChange(request).toPromise();

        // Only submit if affected items were provided — the backend
        // requires at least one affected item to transition out of Draft.
        if (submitAfterCreate && this.canSubmit && change) {
          await this.api.submitChange(change.id).toPromise();
          // Reload to get updated state
          change = await this.api.getChange(change.id).toPromise();
        }
      }

      if (change) {
        this.saved.emit(change);
      }
    } catch (error: any) {
      this.errorMessage = error.error?.error || error.message || 'Failed to save change';
    } finally {
      this.isSaving = false;
    }
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  getTypeDescription(type: ChangeType): string {
    switch (type) {
      case ChangeType.ECR:
        return 'Proposal for a change - requires approval before implementation';
      case ChangeType.ECO:
        return 'Formal order to implement approved changes';
      case ChangeType.Deviation:
        return 'Temporary departure from approved specifications';
      case ChangeType.ECN:
        return 'Notice of completed changes for reference';
      default:
        return '';
    }
  }
}
